/**
 * Deterministic avatar config from a stable seed string.
 *
 * The design canvas uses `<PresetAvatar name="jo" />`; this app has no real "preset"
 * system, so a faithful translation is to seed the existing `Avataaar` from a stable
 * string (same name → same face). Extracted from HomeView's inline seeding so marketing
 * and lobby surfaces share one implementation.
 */
import type { AvatarConfig } from '@/components/Avataaar.vue'

const topTypes = [
  'ShortHairShortFlat', 'ShortHairShortWaved', 'ShortHairDreads01', 'LongHairStraight',
  'LongHairCurly', 'LongHairBun', 'Hat', 'Hijab', 'Turban', 'WinterHat1',
]
const hairColors = [
  'Black', 'Brown', 'BrownDark', 'Blonde', 'BlondeGolden', 'Red', 'Auburn', 'PastelPink', 'Blue',
]
const accessoriesTypes = [
  'Blank', 'Sunglasses', 'Prescription01', 'Prescription02', 'Round', 'Wayfarers',
]
const facialHairTypes = [
  'Blank', 'BeardLight', 'BeardMedium', 'BeardMagestic', 'MoustacheFancy', 'MoustacheMagnum',
]
const clotheTypes = [
  'BlazerShirt', 'Hoodie', 'Overall', 'ShirtCrewNeck', 'ShirtScoopNeck', 'ShirtVNeck',
  'CollarSweater', 'GraphicShirt',
]
const clotheColors = [
  'Black', 'Blue01', 'Blue02', 'Blue03', 'Gray01', 'Gray02', 'Heather', 'PastelBlue',
  'PastelGreen', 'PastelOrange', 'PastelRed', 'PastelYellow', 'Pink', 'Red', 'White',
]
const eyeTypes = ['Default', 'Happy', 'Side', 'Squint', 'Surprised', 'Wink', 'WinkWacky']
const eyebrowTypes = [
  'Default', 'DefaultNatural', 'FlatNatural', 'RaisedExcited', 'UnibrowNatural', 'UpDown',
]
const mouthTypes = ['Default', 'Smile', 'Twinkle', 'Eating', 'Grimace', 'Serious', 'Tongue']
const skinColors = ['Tanned', 'Yellow', 'Pale', 'Light', 'Brown', 'DarkBrown', 'Black']
// Ring colors aligned to the Up North accent palette.
const circleColors = [
  '#C2502F', '#2F4636', '#D9A441', '#4A6D72', '#7A4A32', '#406149', '#5B3724',
]

function stringToSeed(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash) + 1
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

function pick<T>(arr: T[], seed: number): T {
  return arr[Math.floor(seededRandom(seed) * arr.length)]
}

/** Build a deterministic avatar config from a numeric seed. */
export function avatarConfigFromSeed(index: number): AvatarConfig {
  return {
    isCircle: true,
    circleColor: pick(circleColors, index * 67),
    topType: pick(topTypes, index * 17),
    hairColor: pick(hairColors, index * 23),
    accessoriesType: pick(accessoriesTypes, index * 31),
    facialHairType: pick(facialHairTypes, index * 37),
    facialHairColor: pick(hairColors, index * 37 + 7),
    clotheType: pick(clotheTypes, index * 41),
    clotheColor: pick(clotheColors, index * 43),
    eyeType: pick(eyeTypes, index * 47),
    eyebrowType: pick(eyebrowTypes, index * 53),
    mouthType: pick(mouthTypes, index * 59),
    skinColor: pick(skinColors, index * 61),
  }
}

/** Build a deterministic avatar config from a stable seed string (e.g. a name). */
export function avatarConfigFromName(name: string): AvatarConfig {
  return avatarConfigFromSeed(stringToSeed(name))
}
